'use strict';

/**
 * limit-controls — Manager-side helpers for the generic "Show" amount field.
 *
 * Two problems this solves, both for the Achievers/Workday item-selection form:
 *
 *  1. healSelfWritingExpressions(appTemplate)
 *     Existing app instances were saved with a copy of the OLD app-template whose
 *     limitValue / spotlightFilter fields had an `expressionProperties` entry that
 *     wrote back to their OWN model key. In angular-formly a field that self-writes
 *     its model key corrupts the hide/binding cycle (the amount input disappears;
 *     the radio selection stops updating). The Manager renders the template stored
 *     ON the app (edit-app.js: `this.appTemplate = app.appTemplate`), so those
 *     instances stay broken even after the schema is re-deployed. Strip the
 *     self-writes from the loaded template so existing apps behave like new ones.
 *
 *  2. clearLimitValueOnModeChange(model, next, prev)
 *     When the "Show" radio changes value, clear the amount so the field
 *     reopens blank for the new window mode (no stale value carried across
 *     calendar/newest/oldest windows or after Everything). Driven by a $watch on
 *     model.limitBy — deliberately NOT
 *     an expressionProperty, which is what caused problem 1.
 */

function isPlainObject(value) {
    return !!value && typeof value === 'object' && !Array.isArray(value);
}

// Recursively strip `expressionProperties["model.<field.key>"]` from every field
// in any nested structure (fields.data array, fields.settings object-of-arrays,
// fieldGroup, etc.). Returns true if anything was removed.
function stripSelfWrites(node) {
    let changed = false;

    if (Array.isArray(node)) {
        node.forEach(function (child) {
            if (stripSelfWrites(child)) changed = true;
        });
        return changed;
    }

    if (!isPlainObject(node)) return false;

    if (node.key && isPlainObject(node.expressionProperties)) {
        var selfKey = 'model.' + node.key;
        if (Object.prototype.hasOwnProperty.call(node.expressionProperties, selfKey)) {
            delete node.expressionProperties[selfKey];
            changed = true;
        }
    }

    // repeatSection / repeatSectionTable nest child fields under
    // templateOptions.fields (rendered as a nested formly-form), so a self-write
    // there must also be healed. Recurse into that specific subtree, but NOT into
    // the rest of templateOptions (options/label/etc. are data, not fields).
    if (node.templateOptions && Array.isArray(node.templateOptions.fields)) {
        if (stripSelfWrites(node.templateOptions.fields)) changed = true;
    }

    Object.keys(node).forEach(function (key) {
        if (key === 'expressionProperties' || key === 'templateOptions') return;
        var value = node[key];
        if (value && typeof value === 'object') {
            if (stripSelfWrites(value)) changed = true;
        }
    });

    return changed;
}

function healSelfWritingExpressions(appTemplate) {
    if (!appTemplate || !appTemplate.fields) return false;
    return stripSelfWrites(appTemplate.fields);
}

function clearLimitValueOnModeChange(model, next, prev) {
    // Only act on a genuine change of the mode (ignore the initial watch, where
    // prev === next, and no-op typing in the amount field which doesn't change
    // limitBy). Any real change clears the amount.
    if (!model) return;
    if (prev === undefined || next === prev) return;
    model.limitValue = undefined;
}

var limitControls = {
    healSelfWritingExpressions: healSelfWritingExpressions,
    clearLimitValueOnModeChange: clearLimitValueOnModeChange,
};

if (typeof module !== 'undefined' && typeof exports !== 'undefined' && module.exports === exports) {
    module.exports = limitControls;
}

if (typeof angular !== 'undefined') {
    angular.module('rocket.components').value('limitControls', limitControls);
}
